import React, { useState } from 'react';
import {
  Box, ButtonBase, Chip, Container, Divider, Drawer, IconButton, List, ListItemButton, ListItemIcon,
  ListItemText, Menu, MenuItem, SvgIcon, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DiamondOutlinedIcon from '@mui/icons-material/DiamondOutlined';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { useLogo } from '../../../config/useLogo';
import { useIsDesktop } from '../../../common/hooks/useResponsive';
import { useAppSelector } from '../../../store/hooks';
import { selectShowDashboardToolbar } from '../../../store/slices/dashboardSlice';
import OptimusButton from '../../../common/library/Button/Button';
import { TestIds } from '../../../assets/testIds';

const HEADER_HEIGHT = 90;
const DRAWER_WIDTH = 329;
const ACCOUNT_MENU_WIDTH = 232;
const MORE_MENU_WIDTH = 200;

// Header type scale from the Figma 1440 / 1190 / 1000 frames. Dropdown items match the nav
// text size at each step. Tablet and mobile (drawer, account menu) use the large scale.
const LARGE_NAV_MIN_WIDTH = 1440;
const MEDIUM_NAV_MIN_WIDTH = 1190;

interface TopNavSize {
  fontSize: number;
  iconSize: number;
  // Spacing values are theme.spacing() multipliers
  itemGap: number;
  menuItemPaddingY: number;
  actionFontSize: number;
}

const TOP_NAV_SIZES: Record<'large' | 'medium' | 'small', TopNavSize> = {
  large: {
    fontSize: 16, iconSize: 20, itemGap: 2.5, menuItemPaddingY: 1, actionFontSize: 14,
  },
  medium: {
    fontSize: 14, iconSize: 20, itemGap: 1.25, menuItemPaddingY: 1, actionFontSize: 12,
  },
  small: {
    fontSize: 12, iconSize: 15, itemGap: 1.25, menuItemPaddingY: 0.6875, actionFontSize: 12,
  },
};

const getTopNavSize = (isDesktop: boolean, isLargeNav: boolean, isMediumNav: boolean) => {
  if (!isDesktop || isLargeNav) return TOP_NAV_SIZES.large;
  if (isMediumNav) return TOP_NAV_SIZES.medium;
  return TOP_NAV_SIZES.small;
};

export interface TopNavItem {
  id: string;
  name: string;
  path: string;
  Icon: typeof SvgIcon;
  // Replaces Icon while the item is the current page
  FilledIcon?: typeof SvgIcon;
  isCurrent?: boolean;
  onClick?: () => void;
}

export interface TopNavAccountItem extends TopNavItem {
  actionLabel?: string;
  onActionClick?: () => void;
  isDestructive?: boolean;
  hasDividerBefore?: boolean;
}

export interface DashboardTopNavLayoutProps {
  children: JSX.Element;
  navItems: TopNavItem[];
  // Paid tier pages. Always grouped under "More" on desktop regardless of screen width.
  moreItems: TopNavItem[];
  accountItems: TopNavAccountItem[];
  accountName: string;
  accountEmail: string;
  tierLabel?: string;
}

const withCurrentIcon = <T extends TopNavItem>(items: T[]): T[] => items.map((item) => (
  item.isCurrent && item.FilledIcon ? { ...item, Icon: item.FilledIcon } : item
));

export function DashboardTopNavLayout({
  children,
  navItems: navItemsProp,
  moreItems: moreItemsProp,
  accountItems: accountItemsProp,
  accountName,
  accountEmail,
  tierLabel,
}: DashboardTopNavLayoutProps) {
  const navItems = withCurrentIcon(navItemsProp);
  const moreItems = withCurrentIcon(moreItemsProp);
  const accountItems = withCurrentIcon(accountItemsProp);
  const isAccountPageCurrent = accountItems.some((item) => item.isCurrent);
  const theme = useTheme();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const isLargeNav = useMediaQuery(theme.breakpoints.up(LARGE_NAV_MIN_WIDTH));
  const isMediumNav = useMediaQuery(theme.breakpoints.up(MEDIUM_NAV_MIN_WIDTH));
  const navSize = getTopNavSize(isDesktop, isLargeNav, isMediumNav);
  const showAccountLabel = isDesktop && isLargeNav;
  const { secondaryOne: NavLogo } = useLogo();
  const showDashboardToolbar = useAppSelector(selectShowDashboardToolbar);

  const [moreAnchorEl, setMoreAnchorEl] = useState<HTMLElement | null>(null);
  const [accountAnchorEl, setAccountAnchorEl] = useState<HTMLElement | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const closeAllMenus = () => {
    setMoreAnchorEl(null);
    setAccountAnchorEl(null);
    setIsDrawerOpen(false);
  };

  const handleItemClick = (item: TopNavItem) => {
    closeAllMenus();
    if (item.onClick) {
      item.onClick();
    } else {
      navigate(item.path);
    }
  };

  // MUI state tokens: action/hover for hover, action/selected for down/selected (current page,
  // open menu). Both states swap the text from text/secondary to text/primary.
  const hoverBackground = theme.palette.action.hover;
  const selectedBackground = theme.palette.action.selected;
  const getItemColor = (isCurrent?: boolean) => (
    isCurrent ? theme.palette.text.primary : theme.palette.text.secondary
  );
  const openMoreMenu = (event: React.MouseEvent<HTMLButtonElement>) => (
    setMoreAnchorEl(event.currentTarget)
  );
  const openAccountMenu = (event: React.MouseEvent<HTMLButtonElement>) => (
    setAccountAnchorEl(event.currentTarget)
  );

  const navIconProps = { sx: { fontSize: navSize.iconSize } };
  const navTextSx = {
    fontSize: `${navSize.fontSize}px`,
    lineHeight: 1.5,
    letterSpacing: '0.15px',
  };

  const navButtonSx = (isActive: boolean) => ({
    ...navTextSx,
    height: 'auto',
    padding: theme.spacing(1.25),
    borderRadius: theme.spacing(1.5),
    fontWeight: 400,
    whiteSpace: 'nowrap',
    color: isActive ? theme.palette.text.primary : theme.palette.text.secondary,
    backgroundColor: isActive ? selectedBackground : 'transparent',
    // This replaces the link variant's hover/active blocks, so the theme's contained
    // button gradient has to be cleared here too
    '&:hover': {
      color: theme.palette.text.primary,
      backgroundColor: isActive ? selectedBackground : hoverBackground,
      backgroundImage: 'none',
    },
    '&:active': {
      color: theme.palette.text.primary,
      backgroundColor: selectedBackground,
      backgroundImage: 'none',
      transform: 'none',
    },
  });

  // Menu and drawer rows set their text/icon colors on the children, so hover and down
  // states swap those children to text/primary. Destructive rows (Log out) keep their color.
  const itemStateSx = (isCurrent?: boolean, isDestructive?: boolean) => {
    const primaryChildrenSx = isDestructive ? {} : {
      '& .MuiTypography-root, & .MuiListItemIcon-root .MuiSvgIcon-root': {
        color: theme.palette.text.primary,
      },
    };
    return {
      backgroundColor: isCurrent ? selectedBackground : 'transparent',
      '&:hover': {
        backgroundColor: isCurrent ? selectedBackground : hoverBackground,
        ...primaryChildrenSx,
      },
      '&:active, &.Mui-selected, &.Mui-selected:hover, &.Mui-focusVisible': {
        backgroundColor: selectedBackground,
        ...primaryChildrenSx,
      },
    };
  };

  const menuPaperSx = {
    mt: theme.spacing(1),
    borderRadius: theme.spacing(0.5),
    boxShadow: theme.shadows[1],
  };

  const menuItemSx = (isCurrent?: boolean, isDestructive?: boolean) => ({
    padding: `${theme.spacing(navSize.menuItemPaddingY)} ${theme.spacing(2)}`,
    minHeight: 'auto',
    alignItems: 'center',
    ...itemStateSx(isCurrent, isDestructive),
    '& .MuiListItemIcon-root': { minWidth: theme.spacing(7) },
  });

  const renderAccountMenu = () => (
    <Menu
      anchorEl={accountAnchorEl}
      open={Boolean(accountAnchorEl)}
      onClose={() => setAccountAnchorEl(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      data-testid={TestIds.topNavigation.accountMenu}
      MenuListProps={{ disablePadding: true }}
      PaperProps={{ sx: { ...menuPaperSx, width: ACCOUNT_MENU_WIDTH } }}
    >
      <Box sx={{ padding: `${theme.spacing(1)} ${theme.spacing(2)}` }}>
        <Typography variant="body1" sx={{ color: theme.palette.text.primary, wordBreak: 'break-word' }}>
          {accountName}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: theme.spacing(1.75),
            lineHeight: 1.43,
            letterSpacing: '0.17px',
            color: theme.palette.text.secondary,
            wordBreak: 'break-word',
          }}
        >
          {accountEmail}
        </Typography>
        {tierLabel && (
          <Chip
            label={tierLabel}
            size="small"
            sx={{
              mt: theme.spacing(1),
              backgroundColor: theme.palette.brand.primary,
              color: theme.palette.common.white,
            }}
          />
        )}
      </Box>
      <Divider sx={{ my: '0 !important' }} />
      {accountItems.map(({ Icon, ...item }) => [
        item.hasDividerBefore && <Divider key={`${item.id}-divider`} sx={{ my: '0 !important' }} />,
        <MenuItem
          key={item.id}
          data-testid={`${TestIds.topNavigation.accountItemPrefix}-${item.id}`}
          onClick={() => handleItemClick({ Icon, ...item })}
          sx={menuItemSx(item.isCurrent, item.isDestructive)}
        >
          <ListItemIcon>
            <Icon
              sx={{
                fontSize: navSize.iconSize,
                color: item.isDestructive ? theme.palette.error.main : getItemColor(item.isCurrent),
              }}
            />
          </ListItemIcon>
          <ListItemText
            disableTypography
            sx={{ my: theme.spacing(0.5) }}
          >
            <Typography
              variant="body1"
              sx={{
                ...navTextSx,
                color: item.isDestructive ? theme.palette.error.main : getItemColor(item.isCurrent),
              }}
            >
              {item.name}
            </Typography>
            {item.actionLabel && item.onActionClick && (
              <OptimusButton
                customVariant="link"
                fullWidth={false}
                data-testid={TestIds.topNavigation.upgradeLnk}
                buttonText={item.actionLabel}
                leadingIcon={DiamondOutlinedIcon}
                iconProps={{ sx: { fontSize: navSize.actionFontSize + 4 } }}
                iconSpace={theme.spacing(0.625)}
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                  event.stopPropagation();
                  closeAllMenus();
                  item.onActionClick?.();
                }}
                sx={{
                  fontSize: `${navSize.actionFontSize}px`,
                  fontWeight: 400,
                  textDecoration: 'underline',
                  '&:hover': { textDecoration: 'underline', backgroundColor: 'transparent', backgroundImage: 'none' },
                }}
              />
            )}
          </ListItemText>
        </MenuItem>,
      ])}
    </Menu>
  );

  const renderMoreMenu = () => (
    <Menu
      anchorEl={moreAnchorEl}
      open={Boolean(moreAnchorEl)}
      onClose={() => setMoreAnchorEl(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      data-testid={TestIds.topNavigation.moreMenu}
      MenuListProps={{ disablePadding: true }}
      PaperProps={{ sx: { ...menuPaperSx, minWidth: MORE_MENU_WIDTH } }}
    >
      {moreItems.map(({ Icon, ...item }) => (
        <MenuItem
          key={item.id}
          data-testid={`${TestIds.topNavigation.moreItemPrefix}-${item.id}`}
          onClick={() => handleItemClick({ Icon, ...item })}
          sx={menuItemSx(item.isCurrent)}
        >
          <ListItemIcon>
            <Icon sx={{ fontSize: navSize.iconSize, color: getItemColor(item.isCurrent) }} />
          </ListItemIcon>
          <ListItemText
            primary={item.name}
            sx={{ my: theme.spacing(0.5) }}
            primaryTypographyProps={{
              variant: 'body1',
              sx: { ...navTextSx, color: getItemColor(item.isCurrent) },
            }}
          />
        </MenuItem>
      ))}
    </Menu>
  );

  const renderDrawer = () => (
    <Drawer
      anchor="left"
      open={isDrawerOpen}
      onClose={() => setIsDrawerOpen(false)}
      data-testid={TestIds.topNavigation.drawer}
      sx={{ zIndex: theme.zIndex.appBar - 1 }}
      PaperProps={{
        sx: {
          width: DRAWER_WIDTH,
          top: HEADER_HEIGHT + 1,
          height: `calc(100% - ${HEADER_HEIGHT + 1}px)`,
          boxShadow: theme.shadows[1],
        },
      }}
    >
      <List disablePadding sx={{ pt: theme.spacing(1) }}>
        {[...navItems, ...moreItems].map(({ Icon, ...item }) => (
          <ListItemButton
            key={item.id}
            data-testid={`${TestIds.topNavigation.drawerItemPrefix}-${item.id}`}
            selected={item.isCurrent}
            onClick={() => handleItemClick({ Icon, ...item })}
            sx={{
              minHeight: 48,
              px: theme.spacing(2),
              ...itemStateSx(item.isCurrent),
              '& .MuiListItemIcon-root': { minWidth: theme.spacing(7) },
            }}
          >
            <ListItemIcon>
              <Icon sx={{ color: getItemColor(item.isCurrent) }} />
            </ListItemIcon>
            <ListItemText
              primary={item.name}
              primaryTypographyProps={{
                variant: 'body1',
                sx: { color: getItemColor(item.isCurrent) },
              }}
            />
          </ListItemButton>
        ))}
      </List>
    </Drawer>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>
      <Box
        component="header"
        data-testid={TestIds.topNavigation.header}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar,
          height: HEADER_HEIGHT,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(3.75),
          px: theme.spacing(2.5),
          backgroundColor: theme.palette.common.white,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {!isDesktop && (
          <IconButton
            aria-label={`${t('navigation.openMenu')}`}
            data-testid={TestIds.topNavigation.hamburgerBtn}
            onClick={() => setIsDrawerOpen(!isDrawerOpen)}
            sx={{
              padding: theme.spacing(1.25),
              borderRadius: theme.spacing(1.5),
              flexShrink: 0,
              color: isDrawerOpen ? theme.palette.text.primary : theme.palette.text.secondary,
              backgroundColor: isDrawerOpen ? selectedBackground : 'transparent',
              '&:hover': {
                color: theme.palette.text.primary,
                backgroundColor: isDrawerOpen ? selectedBackground : hoverBackground,
              },
              '&:active': {
                color: theme.palette.text.primary,
                backgroundColor: selectedBackground,
              },
            }}
          >
            <MenuIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}

        {NavLogo && (
          <ButtonBase
            onClick={() => navItems[0] && handleItemClick(navItems[0])}
            sx={{ flexShrink: 0, borderRadius: 1 }}
          >
            <Box
              component="img"
              src={NavLogo as string}
              alt="Logo"
              sx={{ height: 34, width: 175 }}
            />
          </ButtonBase>
        )}

        {isDesktop && (
          <Box
            component="nav"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing(navSize.itemGap),
              flex: '1 1 auto',
              minWidth: 0,
            }}
          >
            {navItems.map(({ Icon, ...item }) => (
              <OptimusButton
                key={item.id}
                customVariant="link"
                fullWidth={false}
                data-testid={`${TestIds.topNavigation.navItemPrefix}-${item.id}`}
                buttonText={item.name}
                leadingIcon={Icon}
                iconProps={navIconProps}
                iconSpace={theme.spacing(1)}
                onClick={() => handleItemClick({ Icon, ...item })}
                sx={navButtonSx(!!item.isCurrent)}
              />
            ))}
            {moreItems.length > 0 && (
              <OptimusButton
                customVariant="link"
                fullWidth={false}
                data-testid={TestIds.topNavigation.moreBtn}
                buttonText={`${t('navigation.more')}`}
                leadingIcon={MoreHorizIcon}
                iconProps={navIconProps}
                iconSpace={theme.spacing(1)}
                onClick={openMoreMenu}
                sx={navButtonSx(!!moreAnchorEl || moreItems.some((item) => item.isCurrent))}
              />
            )}
          </Box>
        )}

        <Box sx={{
          display: 'flex', alignItems: 'center', gap: theme.spacing(1), ml: 'auto',
        }}>
          <OptimusButton
            customVariant="link"
            fullWidth={false}
            data-testid={TestIds.topNavigation.accountBtn}
            buttonText={showAccountLabel ? `${t('navigation.account')}` : ''}
            aria-label={`${t('navigation.account')}`}
            leadingIcon={AccountCircleIcon}
            trailingIcon={ExpandMoreIcon}
            iconProps={{ sx: { fontSize: 20 } }}
            iconSpace={theme.spacing(1)}
            onClick={openAccountMenu}
            sx={{
              ...navButtonSx(!!accountAnchorEl || isAccountPageCurrent),
              gap: theme.spacing(1),
            }}
          />
        </Box>
      </Box>

      {renderAccountMenu()}
      {isDesktop ? renderMoreMenu() : renderDrawer()}

      {isDesktop ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 0 auto' }}>
          {children}
        </Box>
      ) : (
        <Container
          maxWidth={showDashboardToolbar ? 'lg' : 'md'}
          sx={{ padding: `${theme.spacing(2)} !important` }}
        >
          {children}
        </Container>
      )}
    </Box>
  );
}
